'use client';

import { useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  file: File;
  onSave: (file: File) => void;
  onCancel: () => void;
}

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
`;

export default function ProductImageCropDialog({
  open,
  file,
  onSave,
  onCancel,
}: Props) {
  const imageSrc = useMemo(() => URL.createObjectURL(file), [file]);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState<any>(null);

  useEffect(() => {
    return () => URL.revokeObjectURL(imageSrc);
  }, [imageSrc]);

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedPixels(croppedAreaPixels);
  };

  const createCroppedImage = async () => {
    if (!croppedPixels) return;

    const img = await createImage(imageSrc);

    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 800;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      img,
      croppedPixels.x,
      croppedPixels.y,
      croppedPixels.width,
      croppedPixels.height,
      0,
      0,
      800,
      800
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onSave(new File([blob], 'producto.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.85
    );
  };

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className={SHEET_CLASS}>
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              Imagen del producto
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 pb-5 space-y-4">
          <div className="relative h-64 bg-black rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(+e.target.value)}
            className="w-full accent-[#103254]"
          />
        </div>

        <DialogFooter className="px-4 sm:px-6 pb-5 pt-3 border-t border-gray-100 dark:border-white/10 flex flex-col sm:flex-row gap-3 shrink-0">
          <Button
            variant="outline"
            onClick={onCancel}
            className={DLG_BTN_SECONDARY}
          >
            Cancelar
          </Button>
          <Button onClick={createCroppedImage} className={DLG_BTN_PRIMARY}>
            Guardar imagen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });